/**
 * ChartLegend — имена серий с цветовой меткой.
 *
 * Легенда есть всегда, когда серий больше одной: цвет сам по себе ничего не
 * называет, и без подписи график читается только тем, кто уже знает ответ.
 * Одна серия легенды не требует — её называет заголовок карточки.
 *
 * @format
 */

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {Row} from 'ui/components/layout/Row';
import {text} from 'ui/theme/typography';
import {surfaceVar} from 'ui/theme/surfaceContext';

import {colorVar, seriesVar} from './tokens';
import type {ChartSeries} from './types';

const Label = styled(Box)({
  ...text('body.s'),
  color: surfaceVar('onSurfaceLight'),
  whiteSpace: 'nowrap',
});

// Метка повторяет саму линию: сплошную — сплошной, пунктирную — пунктирной.
// Иначе прогноз в легенде неотличим от факта, а на графике отличим — и
// читателю приходится сопоставлять их самому.
const Dash = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{$color: string; $dashed: boolean}>(({$color, $dashed}) => ({
  width: 14,
  height: 2,
  flexShrink: 0,
  borderRadius: 1,
  background: $dashed
    ? `repeating-linear-gradient(90deg, ${$color} 0 4px, transparent 4px 7px)`
    : $color,
}));

export interface ChartLegendProps {
  series: ChartSeries[];
}

export function ChartLegend({series}: ChartLegendProps) {
  // Серии «только для тултипа» на канве не нарисованы — называть в легенде
  // нечего: метка указывала бы на цвет, которого на графике нет.
  const drawn = series.filter(s => !s.tooltipOnly);
  if (drawn.length < 2) return null;

  return (
    <Row gap='m' wrap='wrap' alignY='center'>
      {drawn.map((s, index) => (
        <Row key={s.id} gap='xs' alignY='center' width='hug'>
          <Dash
            $color={s.color ? colorVar(s.color) : seriesVar(index)}
            $dashed={Boolean(s.dashed)}
            aria-hidden
          />
          <Label>{s.label}</Label>
        </Row>
      ))}
    </Row>
  );
}
